import React, { useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "../BreakDownAccordian/index.css";
import { useAccordionButton } from "react-bootstrap/AccordionButton";
import DataTable, { TableColumn } from "react-data-table-component";

interface Transaction {
  amount: number;
  description: string;
  type: string;
  paymentMethod?: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  } | any;
  transactionTag?: string;
  id?: string;
  walletId?: string;
  status?: string;
}

interface TransactionGroup {
  totalAmount: number;
  transactions: Transaction[];
}

interface DynamicBreakDownAccordionProps {
  transactionData: {
    [key: string]: TransactionGroup;
  };
}

function ContextAwareToggle({ children, eventKey, isExpanded, onToggle }: any) {
  const decoratedOnClick = useAccordionButton(eventKey, () => {
    onToggle(eventKey);
  });

  return (
    <div className="breackdownrtnbtnprice">
      <button
        type="button"
        className="breakdownaccordianbtn"
        onClick={decoratedOnClick}
      >
        <FontAwesomeIcon
          icon={isExpanded ? faChevronDown : faChevronRight}
          className="Arrow-Icon"
          id="brekDownSVG"
        />
        {children}
      </button>
    </div>
  );
}

const DynamicBreakDownAccordion: React.FC<DynamicBreakDownAccordionProps> = ({
  transactionData,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  const handleToggle = (eventKey: string) => {
    setExpanded(expanded === eventKey ? null : eventKey);
  };

  // SAFE CHECK: If no data or empty object, show message
  if (!transactionData || Object.keys(transactionData).length === 0) {
    return (
      <div className="text-center p-4">
        <p className="text-muted mb-0">No transaction data available</p>
      </div>
    );
  }

  // Format transaction title
  const formatTransactionTitle = (key: string) => {
    const titleMapping: { [key: string]: string } = {
      'CurryKingFees': 'Curry King Fees',
      'CartCommissions': 'Cart Commissions',
      'Sales': 'Sales Payments',
      'CartReturns': 'Cart Returns',
      'Refunds': 'Refunds',
      'MarketingSpend': 'Marketing Spend',
      'OtherPayments': 'Other Payments',
      'Earnings': 'My Earnings',
      'sales': 'Sales Transactions',
      'purchase': 'Platform Payouts',
    };
    return titleMapping[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  // SAFE: Filter out any invalid entries
  const paymentAccordianDetails = Object.entries(transactionData)
    .filter(([key, value]) => {
      return value && 
             typeof value === 'object' && 
             value.totalAmount !== undefined && 
             Array.isArray(value.transactions) && 
             (value.totalAmount > 0 || value.transactions.length > 0);
    })
    .map(([key, value], index) => ({
      id: index + 1,
      title: formatTransactionTitle(key),
      totalAmount: `Rs: ${(value.totalAmount || 0).toFixed(2)}`,
      transactions: value.transactions || [],
      transactionType: key,
    }));

  // SAFE: If no valid sections, show message
  if (paymentAccordianDetails.length === 0) {
    return (
      <div className="text-center p-4">
        <p className="text-muted mb-0">No transaction sections available</p>
      </div>
    );
  }

  // Helper function to convert timestamp to date
  const formatDate = (createdAt: any) => {
    try {
      if (!createdAt) return 'N/A';
      
      // Handle Firestore Timestamp
      if (createdAt?.toDate) {
        const date = createdAt.toDate();
        return formatDateObject(date);
      }
      
      // Handle seconds format
      if (createdAt._seconds) {
        const date = new Date(createdAt._seconds * 1000);
        return formatDateObject(date);
      }
      
      // Handle Date object or string
      const date = new Date(createdAt);
      if (!isNaN(date.getTime())) {
        return formatDateObject(date);
      }
    } catch (e) {
      console.warn('Date formatting error:', e);
    }
    return 'Invalid date';
  };

  const formatDateObject = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const transactionColumns: TableColumn<Transaction>[] = [
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      selector: (row) => formatDate(row.createdAt),
      sortable: true,
      width: "180px",
    },
    {
      name: <h6 className="fw-bold">Description</h6>,
      selector: (row) => row.description || 'N/A',
      sortable: false,
      wrap: true,
    },
    {
      name: <h6 className="fw-bold">Type</h6>,
      selector: (row) => row.type || 'N/A',
      sortable: false,
      width: "100px",
    },
    {
      name: <h6 className="fw-bold">Status</h6>,
      selector: (row) => row.status || "Completed",
      cell: (row) => {
        const status = row.status || "Completed";
        const getBadgeVariant = (status: string) => {
          switch (status.toLowerCase()) {
            case 'pending': return 'warning';
            case 'rejected': return 'danger';
            case 'completed': return 'success';
            default: return 'secondary';
          }
        };
        return (
          <span className={`badge bg-${getBadgeVariant(status)}`}>
            {status}
          </span>
        );
      },
      sortable: false,
      width: "120px",
    },    
    {
      name: <h6 className="fw-bold">Amount (Rs.)</h6>,
      selector: (row) => (row.amount || 0).toFixed(2),
      sortable: true,
      right: true,
      width: "130px",
    },
  ];

  return (
    <div>
      <Accordion defaultActiveKey="0" className="breakdownaccordianwhole">
        {paymentAccordianDetails.map((detail) => (
          <Card key={detail.id} className="breakdownaccordiancard">
            <Card.Header className="breakdowncardheader">
              <ContextAwareToggle
                eventKey={detail.id.toString()}
                isExpanded={expanded === detail.id.toString()}
                onToggle={handleToggle}
              >
                <div className="accordion-title-value">
                  <span>{detail.title}</span>
                  <span className={detail.totalAmount.includes('-') ? 'text-danger' : 'text-success'}>
                    {detail.totalAmount}
                  </span>
                </div>
              </ContextAwareToggle>
            </Card.Header>
            <Accordion.Collapse
              eventKey={detail.id.toString()}
              in={expanded === detail.id.toString()}
            >
              <Card.Body className="card-body-breakdown-style">
                <DataTable
                  columns={transactionColumns}
                  data={detail.transactions}
                  pagination
                  highlightOnHover
                  noDataComponent={<div className="text-center p-3">No transactions available</div>}
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 20, 30]}
                />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        ))}
      </Accordion>
    </div>
  );
};

export default DynamicBreakDownAccordion;